// Modules
import React, { useEffect, useState, useContext } from "react";
import Skeleton from "@material-ui/lab/Skeleton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Collapse from "@material-ui/core/Collapse";
import IconButton from "@material-ui/core/IconButton";
import PropTypes from "prop-types";

// Context
import ApiContext from "../../../contexts/ApiContext";

// Local
import Tooltip from "../../utils/Tooltip";

// Assets
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from '@material-ui/icons/Remove';

const CustomTableRow = (props) => {
	const {
		row,
		loader,
		keyProp,
	} = props;
	const { isMobile, isTablet } = useContext(ApiContext);
	const [open, setOpen] = useState(false);
	const [isScrolling, setIsScrolling] = useState(false);
	const [total, setTotal] = useState(null);
	const [tooltipHeaderElem, setTooltipHeaderElem] = useState(null);
	const tooltips = [
		{
			key: "prix-vente-fai",
			label: "Prix de vente FAI",
			descr: "Prix net vendeur du bien + les frais d’agence (FAI = Frais d’Agence Inclus)",
		},
		{
			key: "frais-bancaire",
			label: "+ Frais garantie bancaire",
			descr: "Montant variable exigé par la banque afin de garantir le prêt. 2 type de garantie differents : cautionnement (Crédit logement / SACEF / CMH) ou réelle (IPPD) : Si cautionnement, possibilité de récupérer une partie des frais à la fin de vie du crédit. Si garantie réelle, aucun montant récupérable.",
		},
		{
			key: "amenagement-ttc",
			label: "Aménagement TTC",
			descr: "Devis proposés par nos partenaires locaux pour la rénovation et l’aménagement du bien. Le montant indiqué est à considérer avec +/- 20% d’écart.",
		},
		{
			key: "frais-gestion-locative",
			label: "Frais de gestion locative",
			descr: "Les frais de mise en location sont réglementés. Il sont compris entre 8 et 13€/m2. Frais à prévoir uniquement lors du changement de locataire. Durée moyenne d’occupation d’un bien prise en compte ici : 2 ans. Hypothèse conservatrice. Frais de gestion modélisés : 6.5% TTC du loyer HC.",
		},
		{
			key: "frais-mise-location",
			label: "Frais de mise en location",
			descr: "Les frais de mise en location sont réglementés. Il sont compris entre 8 et 13€/m2. Frais à prévoir uniquement lors du changement de locataire. Durée moyenne d’occupation d’un bien prise en compte ici : 2 ans. Hypothèse conservatrice. Frais de gestion modélisés : 6.5% TTC du loyer HC.",
		},
		{
			key: "provision-depenses-imprevues",
			label: "Provisions pour dépenses imprévues",
			descr: "Montant pris en compte dans le budget pour couvrir d'éventuelles dépenses de réparation, entretien. Dépend du type de location, du type de bien ainsi que de la surface. Hypothèse conservatrice.",
		},
		{
			key: "vacance-locative",
			label: "Vacance locative estimée (0.5 mois)",
			descr: "Période entre deux locataires durant laquelle le bien n’est pas loué. Varie selon le type de stratégie. Hypothèse conservatrice.",
		},
		{
			key: "revente",
			label: "Revente (= Gain net)",
			descr: "Gain net que procure cet investissement avant impôts sur X ans. Gain net sur X ans = Prix de revente +/- (Gain/Épargne net mensuel x12 x Xans) - Mise de départ",
		},
		{
			key: "valorisation-annuelle",
			label: "Valorisation annuelle du bien",
			descr: "Hypothèse conservatrice de prise de valeur du marché de l’immobilier où se situe le bien en question. Varie suivant la stratégie d’investissement : prise de valeur certaine (Placement : 1.5%), probable (Équilibrée : 1% - correspond à l’évolution moyenne du marché sur les 40 dernières années), variable (Développement : 0.5%).",
		},
		{
			key: "prix-revente",
			label: "+ Prix de revente",
			descr: "Prix de vente modélisé au bout de X ans de possession du bien. Il équivaut au prix d’achat du bien ainsi que la moitié du montant des travaux investis revalorisé chaque année de 1.5 %.",
		},
		{
			key: "location",
			label: "Location (= Rente nette dans 20 ans*)",
			descr: "Elle correspond aux revenus nets perçus une fois le crédit remboursé et l’ensemble de toutes les charges (récurrentes et aléatoires) déduites avant impôts. Rente annuelle = (Total des revenus mensuels - charges récurrentes - charges aléatoires) x12",
		},
	];

	/** 
	 * Format the value depending on which heading it is.
	 */
	useEffect(() => {
		if (row.total !== undefined) {
			const moneyOptions = { style: "currency", currency: "EUR", maximumFractionDigits: 0, minimumFractionDigits: 0 };

			if (row.rubrique.toLowerCase().includes("taux") === false
				&& row.rubrique.toLowerCase().includes("rendement") === false
				&& row.rubrique !== "- Épargne mensuelle placée ou cashflow")
				setTotal(new Intl.NumberFormat("fr-FR", moneyOptions).format(row.total));
			else if (row.rubrique === "- Épargne mensuelle placée ou cashflow")
				setTotal(`${new Intl.NumberFormat("fr-FR", moneyOptions).format(row.total)}/mois`);
			else
				setTotal(`${row.total}${row.total % 1 === 0 ? ".00" : ""} %`);
		}

		if (row?.rubrique) {
			const elem = tooltips.find((elem) => elem.label === row.rubrique);

			if (elem !== undefined)
				setTooltipHeaderElem(elem);
		}
	}, [row.total, row.rubrique]); // eslint-disable-line

	/**
	 * componentDidMount() that listens to scrolling events and helps
	 */
	useEffect(() => {
		const onScrollStartHandler = () => {
			setIsScrolling(true);
		};

		const onScrollEndHandler = () => {
			setIsScrolling(false);
		};

		if (isMobile === true || isTablet === true) {
			document.addEventListener("touchstart", onScrollStartHandler);
			document.addEventListener("touchend", onScrollEndHandler);
		}

		return () => {
			if (isMobile === false && isTablet === false)
				return;
			document.removeEventListener("touchstart", onScrollStartHandler);
			document.removeEventListener("touchend", onScrollEndHandler);
		}
	}, []); // eslint-disable-line

	return (
		<React.Fragment key={keyProp}>
			{/* Header */}
			<TableRow className={`row-custom-header ${keyProp} ${open === true ? "collapsed" : ""}`} key={`${keyProp}-1`}>
				<TableCell className="cell-header-custom expand-icon">
					{row.detail.length > 0
						? (
							<IconButton
								aria-label="expand row"
								size="small"
								onClick={() => setOpen(!open)}
							>
								{open === true ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
							</IconButton>
						) : null}
				</TableCell>
				{loader === true
					? (
						<TableCell
							component="th"
							scope="row"
							className="cell-header-custom"
						>
							<Skeleton
								animation="wave"
								variant="text"
								width="20vh"
							/>
						</TableCell>
					) : (
						<React.Fragment>
							<TableCell
								component="th"
								scope="row"
								className="cell-header-custom"
							>
								{tooltipHeaderElem !== null
									? (
										<Tooltip
											visibleText={tooltipHeaderElem.label}
											tooltipText={tooltipHeaderElem.descr}
											loader={loader}
											keyProp={tooltipHeaderElem.key}
											className="tooltip-header-elem"
											isScrolling={isScrolling}
										/>
									) : row.rubrique}
							</TableCell>
							<TableCell align="right" className={`cell-header-custom value ${row?.hasChanged === true ? "just-changed" : ""}`}>
								{total}
							</TableCell>
						</React.Fragment>
					)}
			</TableRow>

			{/* Body */}
			<TableRow className="row-custom-body" key={`${keyProp}-2`}>
				<TableCell className="cell-first-custom" colSpan={6}>
					<Collapse in={open} timeout="auto" unmountOnExit disableStrictModeCompat>
						<TableContainer>
							<Table aria-label="collapsible table">
								<TableBody>
									{row.detail.map((detailRow) => {
										let value = null;
										const formatOptions = { style: "currency", currency: "EUR", maximumFractionDigits: 0, minimumFractionDigits: 0 };
										let tooltipElem = null;

										if (detailRow.val !== undefined) {
											if (detailRow.poste.toLowerCase().includes("taux") === false
												&& detailRow.poste.toLowerCase().includes("rendement") === false
												&& detailRow.poste !== "- Épargne mensuelle placée ou cashflow"
												&& detailRow.poste !== "Valorisation annuelle du bien")
												value = new Intl.NumberFormat("fr-FR", formatOptions).format(detailRow.val);
											else if (detailRow.poste === "- Épargne mensuelle placée ou cashflow")
												value = `${new Intl.NumberFormat("fr-FR", formatOptions).format(detailRow.val)}/mois`;
											else
												value = `${detailRow.val}${detailRow.val % 1 === 0 ? ".00" : ""} %`;
										}

										if (detailRow?.poste) {
											const elem = tooltips.find((elem) => elem.label === detailRow.poste);

											if (elem !== undefined)
												tooltipElem = elem;
										}

										return (
											<TableRow className="second-row-custom" key={detailRow.poste}>
												{loader === true
													? (
														<React.Fragment>
															<TableCell className="cell-custom" component="th" scope="row">
																<Skeleton
																	animation="wave"
																	variant="text"
																	width="20vh"
																/>
															</TableCell>
														</React.Fragment>
													) : (
														<React.Fragment>
															<TableCell
																className="cell-custom"
																component="th"
																scope="row"
															>
																{tooltipElem !== null
																	? (
																		<Tooltip
																			visibleText={tooltipElem.label.includes("+")
																				? (
																					<React.Fragment>
																						<AddIcon className="plus-icon" />
																						{tooltipElem.label.replace("+ ", "")}
																					</React.Fragment>
																				) : tooltipElem.label.includes("-")
																					? (
																						<React.Fragment>
																							<RemoveIcon className="minus-icon" />
																							{tooltipElem.label.replace("- ", "")}
																						</React.Fragment>
																					) : tooltipElem.label.includes("=")
																						? (
																							<React.Fragment>
																								<div className="equal-icon" />
																								{tooltipElem.label.replace("= ", "")}
																							</React.Fragment>
																						) : tooltipElem.label}
																			tooltipText={tooltipElem.descr}
																			loader={loader}
																			keyProp={tooltipElem.key}
																			className="tooltip-elem"
																			isScrolling={isScrolling}
																		/>
																	) :
																	detailRow.poste.includes("+")
																		? (
																			<h6>
																				<AddIcon className="plus-icon" />
																				{detailRow.poste.replace("+ ", "")}
																			</h6>
																		) : detailRow.poste.includes("-")
																			? (
																				<h6>
																					<RemoveIcon className="minus-icon" />
																					{detailRow.poste.replace("- ", "")}
																				</h6>
																			) : detailRow.poste.includes("=")
																				? (
																					<h6>
																						<span className="equal-icon" />
																						{detailRow.poste.replace("= ", "")}
																					</h6>
																				) : detailRow.poste}
															</TableCell>
															<TableCell className={`cell-custom value ${detailRow?.changed === true ? "just-changed" : ""}`}>
																{value}
															</TableCell>
														</React.Fragment>)}
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					</Collapse>
				</TableCell>
			</TableRow>
		</React.Fragment>
	);
};

CustomTableRow.propTypes = {
	loader: PropTypes.bool.isRequired,
	row: PropTypes.exact({
		keyProp: PropTypes.string.isRequired,
		rubrique: PropTypes.string.isRequired,
		detail: PropTypes.array.isRequired,
		total: PropTypes.number,
		hasChanged: PropTypes.bool,
	}).isRequired,
	keyProp: PropTypes.string.isRequired,
};

export default CustomTableRow;